import * as THREE from 'three';

/**
 * Where a point in the world lands on the screen.
 *
 * Two pieces of interface stand over the scene — names above the pawns and
 * progress over the buildings going up — and they ask the same question. The
 * answer is fiddly enough to be worth having once: the canvas sits in the
 * corner of its container and carries a border, so a projection has to be
 * measured against the canvas rather than the window, and a point behind the
 * camera has to be hidden rather than clamped to an edge, where it would label
 * something that is not there.
 */

export interface Placer {
  /** Screen position, or null when it should not be drawn at all. */
  (point: THREE.Vector3): { x: number; y: number } | null;
}

const projected = new THREE.Vector3();

/**
 * A placer for this frame.
 *
 * Made once per frame rather than per label: it reads the canvas rectangle,
 * which is a layout measurement and the one expensive part of this.
 */
export function screenPlacer(
  container: HTMLElement,
  canvas: HTMLCanvasElement,
  camera: THREE.Camera
): Placer {
  const rect = canvas.getBoundingClientRect();
  const parent = container.getBoundingClientRect();

  const left = rect.left - parent.left + canvas.clientLeft;
  const top = rect.top - parent.top + canvas.clientTop;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;

  return (point: THREE.Vector3) => {
    projected.copy(point).project(camera);

    const x = left + ((projected.x + 1) / 2) * width;
    const y = top + ((1 - projected.y) / 2) * height;

    const inside =
      projected.z >= -1 &&
      projected.z <= 1 &&
      x >= left &&
      x <= left + width &&
      y >= top &&
      y <= top + height;

    return inside ? { x, y } : null;
  };
}
